/**
 * Aperçu du menu, mis à l'échelle pour tenir dans la largeur disponible.
 *
 * Le template est rendu à sa largeur de CONCEPTION (`designWidth` : 352 en
 * petit, 600 en grand) puis réduit par `transform: scale()` porté par un
 * conteneur parent — jamais par le noeud cible lui-même. La `ref` transmise
 * pointe le noeud de conception.
 *
 * ⚠️ Le scale de l'ancêtre AFFECTE bien `getBoundingClientRect()` du noeud
 * cible (valeur VISUELLE, réduite) — donc `domToPng(node)` SEUL capturerait au
 * mauvais format et couperait la 2e colonne du grand format (bug fan
 * 2026-07-18). L'export DOIT passer `width/height` = `offsetWidth/offsetHeight`
 * (boîte de layout, insensible aux transforms) pour capturer à la taille
 * pleine. Cf `renderMenuPng` dans MenuTab.tsx.
 *
 * La hauteur du conteneur externe est fixée à la hauteur réelle mise à
 * l'échelle pour ne pas laisser de vide sous l'aperçu quand le contenu
 * change (image chargée, offres ajoutées…).
 *
 * Bug 2026-07-13 (« grand vide sous l'onglet Menu ») : la mesure ne
 * tournait QUE dans le callback d'un `ResizeObserver` observant `outer`/
 * `inner`. En changeant de variante (ex. « La grande carte » → « Toute à
 * toi », même `format`/`designWidth` donc l'effet d'observation ne se
 * ré-arme pas) le CONTENU de `inner` change de hauteur sans que `outer` ou
 * `inner` ne changent de LARGEUR — un resize de hauteur pure. Constaté à
 * l'usage (harnais dev, `menu-dev.html`, cf commit) : dans ce cas précis le
 * `ResizeObserver` ne redéclenche pas de mesure de façon fiable, et l'ancien
 * `height` (calculé pour l'ancien contenu, potentiellement bien plus haut)
 * reste figé — c'est EXACTEMENT le vide constaté (un aperçu « Toute à toi »
 * de 3 offres gardait la hauteur réservée du menu 14 offres précédent, ~270px
 * de trop). Fix : un SECOND effet, sans dépendances, remesure après CHAQUE
 * render (donc à chaque changement du contenu rendu), en plus du
 * `ResizeObserver` qui reste nécessaire pour les resizes externes (panneau
 * redimensionné) qui ne déclenchent aucun re-render React de ce composant.
 * `available<=0` (largeur pas encore connue) → on n'écrit RIEN plutôt que de
 * retomber sur `scale=1` (qui réserverait la hauteur PLEINE non réduite,
 * l'autre façon de produire ce même vide).
 */
import { forwardRef, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";

export const MenuPreview = forwardRef<HTMLDivElement, {
  designWidth: number;
  children: ReactNode;
}>(function MenuPreview({ designWidth, children }, ref) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const measure = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const available = outer.clientWidth;
    // Largeur pas (encore) mesurable — on ne touche à rien plutôt que de
    // réserver la hauteur pleine non réduite (cf note ci-dessus).
    if (available <= 0) return;
    const nextScale = Math.min(1, available / designWidth);
    setScale(nextScale);
    // offsetHeight ignore le transform → hauteur de layout non mise à l'échelle.
    setHeight(inner.offsetHeight * nextScale);
  }, [designWidth]);

  // Remesure après CHAQUE render — capte un changement de HAUTEUR du contenu
  // (variante différente, offres ajoutées/retirées) qui ne fait varier ni
  // `designWidth` ni la largeur de `outer`/`inner` (donc pas forcément
  // rattrapé par le `ResizeObserver` ci-dessous, cf note plus haut).
  // `measure()` est un no-op stable (mêmes valeurs → `setState` bail out),
  // donc pas de boucle : ce n'est PAS le pattern dangereux "setState sans
  // garde à chaque render", juste une lecture DOM + éventuelle correction.
  useLayoutEffect(() => {
    measure();
  });

  // Resize EXTERNE (panneau redimensionné, sidebar togglée…) : ne déclenche
  // aucun re-render React de ce composant, donc l'effet ci-dessus ne
  // tournerait pas — le ResizeObserver reste le seul moyen de le capter.
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div ref={outerRef} className="w-full" style={{ height }}>
      <div
        ref={innerRef}
        style={{ width: designWidth, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
        {/* Noeud de conception : cible directe de domToPng (taille pleine). */}
        <div ref={ref}>{children}</div>
      </div>
    </div>
  );
});
